import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Crown,
  Loader2,
  RefreshCcw,
  Search,
  Mail,
  Phone,
  ExternalLink,
  ChevronDown,
  Lock,
  LogOut,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth } from "../lib/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEAGUE_STYLE = {
  AAMBL: "text-amber-400",
  WAABL: "text-purple-400",
  BOTH: "text-white",
};

const Field = ({ label, value, multiline, isLink }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
      {label}
    </div>
    {value ? (
      isLink ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-amber-300 hover:text-amber-400 inline-flex items-center gap-1.5 mt-1"
        >
          Open link <ExternalLink size={11} />
        </a>
      ) : (
        <div className={`text-neutral-200 mt-1 ${multiline ? "whitespace-pre-wrap" : ""}`}>
          {value}
        </div>
      )
    ) : (
      <div className="text-neutral-500 mt-1">—</div>
    )}
  </div>
);

const Row = ({ app, expanded, onToggle }) => {
  const created = app.created_at ? new Date(app.created_at) : null;
  const leagueClass = LEAGUE_STYLE[app.league_preference] || "text-white";
  return (
    <div data-testid={`admin-own-row-${app.id}`} className="border-b border-neutral-900">
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-12 gap-4 px-5 py-4 text-left hover:bg-[#0a0a0a] transition-colors"
      >
        <div className="col-span-12 md:col-span-3">
          <div className="text-sm text-white">
            {app.first_name} {app.last_name}
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
            {created ? created.toLocaleDateString() : "—"}
          </div>
        </div>
        <div className="col-span-12 md:col-span-2">
          <div className={`text-[10px] uppercase tracking-[0.3em] ${leagueClass}`}>
            {app.league_preference}
          </div>
          <div className="text-xs text-neutral-400 mt-1">
            {app.market_preference || "—"}
          </div>
        </div>
        <div className="col-span-12 md:col-span-3">
          <div className="text-xs text-white truncate">{app.company || "—"}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
            {app.title || "—"}
          </div>
        </div>
        <div className="col-span-12 md:col-span-2">
          <div className="text-xs text-white">{app.investment_range || "—"}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
            {app.ownership_type || "—"}
          </div>
        </div>
        <div className="col-span-12 md:col-span-2 min-w-0">
          <div className="text-xs text-neutral-300 truncate">{app.email}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
            {app.phone}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-6 pt-2 bg-black grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3 text-xs">
            <Field label="City / State" value={app.city_state} />
            <Field label="LinkedIn" value={app.linkedin} isLink />
            <Field label="Website" value={app.website} isLink />
            <Field label="Business Background" value={app.business_background} multiline />
            <div className="flex flex-wrap gap-3 pt-3">
              <a
                href={`mailto:${app.email}?subject=AfroAm%20Ownership%20%E2%80%94%20${encodeURIComponent(app.league_preference)}`}
                className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-300 px-4 py-2 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-amber-400 hover:text-black transition-colors"
              >
                <Mail size={12} /> Email
              </a>
              <a
                href={`tel:${app.phone}`}
                className="inline-flex items-center gap-2 border border-neutral-800 text-neutral-300 px-4 py-2 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
              >
                <Phone size={12} /> Call
              </a>
            </div>
          </div>
          <div className="space-y-3 text-xs">
            <Field label="Sports Background" value={app.sports_background} multiline />
            <Field label="Why AfroAm?" value={app.why_afroam} multiline />
          </div>
        </div>
      )}
    </div>
  );
};

const AdminTeamOwnersPage = () => {
  const { user, loading: authLoading, signIn, signOut } = useAuth();
  const isAuthed = !!user?.is_admin;

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [leagueFilter, setLeagueFilter] = useState("All");
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/team-owners`);
      setApps(Array.isArray(data) ? data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setApps([]);
      } else {
        const detail = err?.response?.data?.detail || "Failed to load inquiries.";
        setError(typeof detail === "string" ? detail : "Failed to load.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/team-owners`);
        if (cancelled) return;
        setApps(Array.isArray(data) ? data : []);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setApps([]);
        } else {
          const detail = err?.response?.data?.detail || "Failed to load inquiries.";
          setError(typeof detail === "string" ? detail : "Failed to load.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((a) => {
      if (leagueFilter !== "All" && a.league_preference !== leagueFilter) return false;
      if (!q) return true;
      const blob = `${a.first_name} ${a.last_name} ${a.email} ${a.company || ""} ${a.market_preference || ""} ${a.investment_range || ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [apps, query, leagueFilter]);

  if (authLoading) {
    return (
      <div className="App bg-black text-white min-h-screen">
        <Navbar />
        <section className="pt-32 md:pt-40 pb-24 min-h-[80vh] flex items-center justify-center">
          <Loader2 size={28} className="text-amber-400 animate-spin" />
        </section>
        <Footer />
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="App bg-black text-white min-h-screen">
        <Navbar />
        <section className="pt-32 md:pt-40 pb-24 min-h-[80vh]">
          <div className="max-w-[820px] mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="admin-own-signin"
              className="border border-amber-400/30 bg-[#0a0a0a] p-8 md:p-12"
            >
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
                — Admin · Restricted
              </div>
              <Lock size={42} className="text-amber-400 mb-6" />
              <h1 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9]">
                Team Owner
                <br />
                <span className="text-amber-400">inquiries.</span>
              </h1>
              <p className="text-neutral-300 mt-6 max-w-xl leading-relaxed">
                Sign in with your AfroAm-allowlisted Google account to review
                ownership inquiries.
              </p>
              <div className="mt-8">
                <button
                  onClick={signIn}
                  className="inline-flex items-center gap-3 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  Sign in with Google
                </button>
              </div>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />
      <section className="pt-32 md:pt-40 pb-12 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Crown size={14} />
            <span>Admin · Team Owner Inquiries</span>
          </div>
          <h1 data-testid="admin-own-title" className="font-display uppercase text-5xl md:text-7xl text-white leading-[0.88]">
            Ownership <span className="text-amber-400">pipeline.</span>
          </h1>
          <p className="text-neutral-400 max-w-xl mt-6 font-light leading-relaxed">
            All AAMBL &amp; WAABL ownership inquiries submitted via{" "}
            <span className="text-white">/team-owners</span>. Newest first.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-12 border-b border-neutral-900">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="admin-own-search"
                  placeholder="Search name, company, market..."
                  className="bg-transparent border border-neutral-800 focus:border-amber-400 text-white text-sm pl-9 pr-4 py-2.5 outline-none placeholder:text-neutral-700 min-w-[260px]"
                />
              </div>
              <select
                value={leagueFilter}
                onChange={(e) => setLeagueFilter(e.target.value)}
                data-testid="admin-own-league-filter"
                className="bg-transparent border border-neutral-800 focus:border-amber-400 text-white text-sm px-3 py-2.5 outline-none [color-scheme:dark]"
              >
                {["All", "AAMBL", "WAABL", "BOTH"].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" data-testid="admin-own-count">
                {filtered.length} / {apps.length} inquiries
              </div>
              <button
                onClick={fetchData}
                data-testid="admin-own-refresh"
                className="inline-flex items-center gap-2 border border-neutral-800 hover:border-amber-400 text-neutral-300 hover:text-amber-300 px-4 py-2.5 text-[10px] uppercase tracking-[0.28em] font-bold transition-colors"
              >
                <RefreshCcw size={12} /> Refresh
              </button>
              <div className="flex items-center gap-2 border border-neutral-900 pl-2 pr-1 py-1.5">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-amber-400/20 text-amber-300 text-[10px] font-bold flex items-center justify-center">
                    {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                  {user?.email}
                </div>
                <button onClick={signOut} className="text-neutral-500 hover:text-amber-300 p-1.5" aria-label="Sign out">
                  <LogOut size={12} />
                </button>
              </div>
            </div>
          </div>

          <div className="border border-neutral-900 bg-[#070707]" data-testid="admin-own-list">
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.3em] text-neutral-500 border-b border-neutral-900 bg-black">
              <div className="col-span-3">Applicant</div>
              <div className="col-span-2">League · Market</div>
              <div className="col-span-3">Company · Title</div>
              <div className="col-span-2">Investment · Type</div>
              <div className="col-span-2">Contact</div>
            </div>

            {loading ? (
              <div className="px-5 py-16 text-center text-neutral-500">
                <Loader2 size={20} className="animate-spin mx-auto mb-3" />
                <div className="text-[10px] uppercase tracking-[0.3em]">Loading inquiries</div>
              </div>
            ) : error ? (
              <div className="px-5 py-12 text-center text-red-400 text-sm">{error}</div>
            ) : filtered.length === 0 ? (
              <div className="px-5 py-16 text-center text-neutral-500">
                <Crown size={20} className="mx-auto mb-3 text-neutral-700" />
                <div className="text-[10px] uppercase tracking-[0.3em]">
                  {apps.length === 0 ? "No inquiries yet" : "No inquiries match your filter"}
                </div>
              </div>
            ) : (
              filtered.map((app) => (
                <Row
                  key={app.id}
                  app={app}
                  expanded={expandedId === app.id}
                  onToggle={() => setExpandedId(expandedId === app.id ? null : app.id)}
                />
              ))
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminTeamOwnersPage;
